import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Copy, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Replicate Assignments Dialog — copies one publisher's full brand
 * roster (rates, splits, modes, passthrough breakdown) to N other
 * publishers in one call. Idempotent: existing target rows are kept.
 *
 * Solves the operational pain "all my pubs use the same 80/20 + 8% rules
 * — don't make me click 70 brands per pub."
 */
export function ReplicateAssignmentsDialog({ open, onClose, sourcePublisher, sourceAssignmentCount, onReplicated }) {
  const [allPubs, setAllPubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  // "empty_only" hides pubs that already have any assignments — what
  // most admins want when onboarding a fresh batch.
  const [emptyOnly, setEmptyOnly] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ("");
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Use the coverage matrix endpoint — gives us per-pub assignment
        // totals so the admin can see who's already configured.
        const { data } = await API.get("/admin/coverage-matrix");
        if (!cancelled) setAllPubs(data.publishers || []);
      } catch (e) {
        toast.error("Failed to load publishers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filteredPubs = useMemo(() => {
    return allPubs
      .filter(p => p.id !== sourcePublisher?.id)            // exclude self
      .filter(p => p.status !== "rejected" && p.status !== "pending_approval")
      .filter(p => !emptyOnly || p.total_assigned === 0)
      .filter(p => {
        const needle = q.trim().toLowerCase();
        if (!needle) return true;
        return (p.email || "").toLowerCase().includes(needle);
      });
  }, [allPubs, sourcePublisher, emptyOnly, q]);

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(filteredPubs.map(p => p.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one target publisher");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/replicate", {
        source_publisher_id: sourcePublisher.id,
        target_publisher_ids: Array.from(selected),
      });
      const msg = `Copied ${data.copied} assignments to ${data.target_results.length} pub${data.target_results.length === 1 ? "" : "s"}`;
      const subMsg = data.skipped_existing
        ? ` · ${data.skipped_existing} skipped (already had them)`
        : "";
      toast.success(msg + subMsg);
      onReplicated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replicate failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="replicate-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Copy size={16} className="text-[#002FA7]" />
            Replicate {sourcePublisher?.email || "this pub"}'s assignments
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Copy {sourceAssignmentCount || 0} brand assignment{sourceAssignmentCount === 1 ? "" : "s"} (rates, splits, payout modes intact) to the publishers you select. Existing rows on the target are preserved — never overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 py-2 border-b border-slate-100">
          <div className="flex items-center gap-1.5 bg-slate-50 rounded p-0.5">
            <MagnifyingGlass size={12} className="text-slate-400 ml-1.5" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Filter by email…"
              className="h-7 text-sm w-56 bg-transparent border-0 shadow-none"
              data-testid="replicate-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={emptyOnly}
              onChange={e => setEmptyOnly(e.target.checked)}
              data-testid="replicate-empty-only"
            />
            Empty pubs only
          </label>
          <span className="ml-auto text-xs text-slate-500">
            {loading ? "Loading…" : `${filteredPubs.length} candidate${filteredPubs.length === 1 ? "" : "s"}`}
          </span>
        </div>

        <div className="flex-1 overflow-auto border border-slate-100 rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
                <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Already has</th>
                <th className="text-center px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filteredPubs.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No publishers match</td></tr>
              )}
              {!loading && filteredPubs.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleRow(p.id)}
                      data-testid={`replicate-row-cb-${p.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900 truncate max-w-[300px]">{p.email}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-mono text-xs ${p.total_assigned === 0 ? "text-slate-400" : "text-slate-700"}`}>
                      {p.total_assigned}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase bg-slate-100 text-slate-600">{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAll} disabled={loading || filteredPubs.length === 0}>Select all</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearAll} disabled={selected.size === 0}>Clear ({selected.size})</Button>
          </div>
          <span className="text-xs text-slate-500">{selected.size} selected</span>
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs" data-testid="replicate-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={selected.size === 0 || submitting}
            className="h-8 text-xs bg-[#002FA7] hover:bg-[#0428A0]"
            data-testid="replicate-submit"
          >
            {submitting
              ? "Replicating…"
              : `Copy ${sourceAssignmentCount || 0} brand${sourceAssignmentCount === 1 ? "" : "s"} → ${selected.size} pub${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
